import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Container,
  Alert,
  Divider,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Link as MuiLink
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import logoPainel from '../imagens/logopainelftm.png.ico';
import { validateBrPhone, maskBrPhone } from '../utils/phoneValidator';

const RegisterContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: theme.palette.background.default,
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  background: '#141926',
  padding: theme.spacing(4),
  borderRadius: '16px',
  border: '1px solid rgba(255,255,255,0.06)',
  maxWidth: '440px',
  width: '100%',
  margin: 'auto',
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

const Register = ({ onLogin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const googleBtnRef = useRef(null);

  const googleState = location.state || {};
  const [googleCredential, setGoogleCredential] = useState(googleState.googleCredential || null);
  const [googleData, setGoogleData] = useState(googleState.googleData || null);

  const [formData, setFormData] = useState({
    nome: googleState.googleData?.nome || '',
    email: googleState.googleData?.email || '',
    senha: '',
    confirmSenha: '',
    cpf: '',
    telefone: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  const isGoogleFlow = !!googleCredential;

  const handleGoogleResponse = useCallback(async (response) => {
    setError('');
    setGoogleLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/google`,
        { credential: response.credential },
        { timeout: 15000 }
      );
      if (res.data.success && res.data.token) {
        localStorage.setItem('user', JSON.stringify(res.data.user));
        localStorage.setItem('token', res.data.token);
        if (window.fbq) window.fbq('track', 'CompleteRegistration', { content_name: 'Painel FTM - Google', status: true });
        onLogin(res.data.user);
        navigate('/dashboard');
      } else if (res.data.needsCpf) {
        setGoogleCredential(response.credential);
        setGoogleData(res.data.googleData);
        setFormData(prev => ({
          ...prev,
          nome: res.data.googleData.nome || '',
          email: res.data.googleData.email || '',
        }));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao autenticar com Google.');
    } finally {
      setGoogleLoading(false);
    }
  }, [navigate, onLogin]);

  useEffect(() => {
    if (isGoogleFlow) return;
    const initGoogle = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
        });
        if (googleBtnRef.current) {
          window.google.accounts.id.renderButton(googleBtnRef.current, {
            type: 'standard',
            theme: 'filled_black',
            size: 'large',
            text: 'signup_with',
            width: '100%',
            locale: 'pt-BR',
          });
        }
      }
    };
    if (window.google?.accounts?.id) {
      initGoogle();
    } else {
      const interval = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(interval);
          initGoogle();
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, [handleGoogleResponse, isGoogleFlow]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === 'cpf') newValue = value.replace(/\D/g, '');
    if (name === 'telefone') {
      newValue = maskBrPhone(value);
      // Valida em tempo real apenas quando o usuário terminou de digitar (11 dígitos)
      const digits = newValue.replace(/\D/g, '');
      if (digits.length === 0 || digits.length === 11) {
        const result = validateBrPhone(newValue);
        setPhoneError(result.valid || digits.length === 0 ? '' : result.error);
      } else {
        setPhoneError('');
      }
    }
    setFormData(prev => ({ ...prev, [name]: newValue }));
  };

  const handleGoogleSubmitCpf = async (e) => {
    e.preventDefault();
    setError('');
    const cpfLimpo = formData.cpf.replace(/\D/g, '');
    if (cpfLimpo.length !== 11) {
      setError('CPF deve conter 11 dígitos');
      return;
    }
    const phoneCheck = validateBrPhone(formData.telefone);
    if (!phoneCheck.valid) {
      setPhoneError(phoneCheck.error);
      setError(phoneCheck.error);
      return;
    }
    if (!acceptTerms) {
      setError('Você precisa aceitar os Termos de Uso e a Política de Privacidade.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/google`,
        { credential: googleCredential, cpf: formData.cpf, telefone: phoneCheck.normalized },
        { timeout: 15000 }
      );
      if (res.data.success && res.data.token) {
        localStorage.setItem('user', JSON.stringify(res.data.user));
        localStorage.setItem('token', res.data.token);
        if (window.fbq) window.fbq('track', 'CompleteRegistration', { content_name: 'Painel FTM - Google CPF', status: true });
        onLogin(res.data.user);
        navigate('/dashboard');
      } else {
        setError(res.data.message || 'Erro ao criar conta');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao criar conta com Google.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (formData.senha !== formData.confirmSenha) {
      setError('As senhas não coincidem');
      return;
    }
    const cpfLimpo = formData.cpf.replace(/\D/g, '');
    if (cpfLimpo.length !== 11) {
      setError('CPF deve conter 11 dígitos');
      return;
    }
    const phoneCheck = validateBrPhone(formData.telefone);
    if (!phoneCheck.valid) {
      setPhoneError(phoneCheck.error);
      setError(phoneCheck.error);
      return;
    }
    if (!acceptTerms) {
      setError('Você precisa aceitar os Termos de Uso e a Política de Privacidade.');
      return;
    }
    setLoading(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: formData.nome, email: formData.email, senha: formData.senha,
          cpf: formData.cpf, telefone: phoneCheck.normalized,
          ref: new URLSearchParams(location.search).get('ref') || undefined
        }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      const data = await response.json();
      if (data.success && data.user && data.token) {
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('token', data.token);
        if (window.fbq) window.fbq('track', 'CompleteRegistration', { content_name: 'Painel FTM', status: true });
        if (onLogin) onLogin(data.user);
        navigate('/dashboard');
      } else {
        setError(data.message || 'Erro ao registrar');
      }
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        setError('Servidor demorou para responder. Tente novamente.');
      } else {
        setError('Erro ao conectar com o servidor. Verifique sua conexão.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Google flow: show only CPF + Telefone form
  if (isGoogleFlow) {
    return (
      <RegisterContainer>
        <StyledPaper elevation={3}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <img src={logoPainel} alt="Painel FTM" style={{ width: 64, height: 64, borderRadius: 12 }} />
          </Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#fff', textAlign: 'center', mb: 1 }}>
            Quase lá!
          </Typography>
          <Typography sx={{ color: '#9CA3AF', textAlign: 'center', mb: 3, fontSize: '0.95rem' }}>
            Olá <strong style={{ color: '#fff' }}>{googleData?.nome}</strong>, precisamos de mais algumas informações para completar seu cadastro.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <form onSubmit={handleGoogleSubmitCpf}>
            <TextField
              fullWidth label="CPF" name="cpf" value={formData.cpf}
              onChange={handleChange} margin="normal" required placeholder="Apenas números"
            />
            <TextField
              fullWidth label="Telefone (WhatsApp)" name="telefone" value={formData.telefone}
              onChange={handleChange} margin="normal" required
              placeholder="(31) 99139-1218"
              inputProps={{ inputMode: 'numeric', maxLength: 16 }}
              error={!!phoneError}
              helperText={phoneError || 'Use seu número de WhatsApp para receber o suporte.'}
            />

            <FormControlLabel
              sx={{ mt: 2, alignItems: 'flex-start', mr: 0 }}
              control={
                <Checkbox
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  sx={{ color: 'rgba(255,255,255,0.4)', mt: '-2px', '&.Mui-checked': { color: '#7C3AED' } }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: '0.85rem', lineHeight: 1.55 }}>
                  Li e concordo com os{' '}
                  <MuiLink href="/termos-de-uso" target="_blank" rel="noopener noreferrer" sx={{ color: '#A78BFA', fontWeight: 600 }}>
                    Termos de Uso
                  </MuiLink>{' '}
                  e a{' '}
                  <MuiLink href="/politica-de-privacidade" target="_blank" rel="noopener noreferrer" sx={{ color: '#A78BFA', fontWeight: 600 }}>
                    Política de Privacidade
                  </MuiLink>
                  . Declaro que utilizarei apenas dados próprios e estou ciente de que o Painel FTM
                  não se responsabiliza por banimentos de BMs ou ações das plataformas terceiras.
                </Typography>
              }
            />

            <Button
              type="submit" fullWidth variant="contained" disabled={loading}
              sx={{
                mt: 3, mb: 2, background: '#7C3AED',
                color: '#fff', padding: '12px', borderRadius: '12px', textTransform: 'none',
                fontSize: '1rem', fontWeight: 600,
                '&:hover': { background: '#6D28D9' }
              }}
            >
              {loading ? 'Criando conta...' : 'Finalizar Cadastro'}
            </Button>
          </form>
        </StyledPaper>
      </RegisterContainer>
    );
  }

  return (
    <RegisterContainer>
      <StyledPaper elevation={3}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <img src={logoPainel} alt="Painel FTM" style={{ width: 64, height: 64, borderRadius: 12 }} />
        </Box>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#fff', textAlign: 'center', mb: 3 }}>
          Criar Conta
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Google Sign-Up */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          {googleLoading ? (
            <CircularProgress size={32} sx={{ color: '#7C3AED', my: 1 }} />
          ) : (
            <Box ref={googleBtnRef} sx={{ width: '100%', display: 'flex', justifyContent: 'center' }} />
          )}
        </Box>

        <Divider sx={{ my: 2, '&::before, &::after': { borderColor: 'rgba(124, 58, 237, 0.2)' } }}>
          <Typography sx={{ color: '#6B7280', fontSize: '0.85rem', px: 1 }}>ou</Typography>
        </Divider>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth label="Nome" name="nome" value={formData.nome}
            onChange={handleChange} margin="normal" required
          />
          <TextField
            fullWidth label="Email" name="email" type="email" value={formData.email}
            onChange={handleChange} margin="normal" required
          />
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              fullWidth label="Senha" name="senha" type="password" value={formData.senha}
              onChange={handleChange} margin="normal" required
            />
            <TextField
              fullWidth label="Confirmar Senha" name="confirmSenha" type="password" value={formData.confirmSenha}
              onChange={handleChange} margin="normal" required
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              fullWidth label="CPF" name="cpf" value={formData.cpf}
              onChange={handleChange} margin="normal" required placeholder="Apenas números"
            />
            <TextField
              fullWidth label="Telefone (WhatsApp)" name="telefone" value={formData.telefone}
              onChange={handleChange} margin="normal" required
              placeholder="(31) 99139-1218"
              inputProps={{ inputMode: 'numeric', maxLength: 16 }}
              error={!!phoneError}
              helperText={phoneError || 'Use seu número de WhatsApp para receber o suporte.'}
            />
          </Box>

          <FormControlLabel
            sx={{ mt: 2, alignItems: 'flex-start', mr: 0 }}
            control={
              <Checkbox
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                sx={{ color: 'rgba(255,255,255,0.4)', mt: '-2px', '&.Mui-checked': { color: '#7C3AED' } }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: '0.85rem', lineHeight: 1.55 }}>
                Li e concordo com os{' '}
                <MuiLink href="/termos-de-uso" target="_blank" rel="noopener noreferrer" sx={{ color: '#A78BFA', fontWeight: 600 }}>
                  Termos de Uso
                </MuiLink>{' '}
                e a{' '}
                <MuiLink href="/politica-de-privacidade" target="_blank" rel="noopener noreferrer" sx={{ color: '#A78BFA', fontWeight: 600 }}>
                  Política de Privacidade
                </MuiLink>
                . Declaro que utilizarei apenas dados próprios e estou ciente de que o Painel FTM
                não se responsabiliza por banimentos de BMs ou ações das plataformas terceiras.
              </Typography>
            }
          />

          <Button
            type="submit" fullWidth variant="contained" disabled={loading}
            sx={{
              mt: 3, mb: 2, background: '#7C3AED',
              color: '#fff', padding: '12px', borderRadius: '12px', fontSize: '1rem', fontWeight: 600,
              '&:hover': { background: '#6D28D9' }
            }}
          >
            {loading ? 'Registrando...' : 'Registrar'}
          </Button>
        </form>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2">
            Já tem uma conta?{' '}
            <Button variant="text" onClick={() => navigate('/login')} sx={{ color: 'primary.main', textTransform: 'none', fontWeight: 600 }}>
              Faça login
            </Button>
          </Typography>
        </Box>
      </StyledPaper>
    </RegisterContainer>
  );
};

export default Register;
